/**
 * Entitlement Sync Hook
 *
 * Syncs license state from renderer stores to the main process entitlement service.
 * The main process is the source of truth for entitlement decisions.
 *
 * This hook watches for changes in:
 * - Trial state (from useLicenseStore)
 * - Subscription state (from useSubscriptionStore)
 *
 * And syncs them to the main process via IPC.
 */

import { useEffect, useRef } from 'react';
import { useLicenseStore } from '../store/licenseStore';
import { useSubscriptionStore } from '../store/subscriptionStore';

/**
 * Convert a Date or null to ISO string or null.
 */
function toIsoOrNull(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  if (typeof date === 'string') return date;
  return date.toISOString();
}

/**
 * Hook that syncs entitlement data to the main process whenever it changes.
 * Should be used in a top-level component (e.g., App.tsx).
 */
export function useEntitlementSync(): void {
  // Access trial data from the nested state object
  const trialStartedAt = useLicenseStore((s) => s.state.trialStartedAt);
  const trialExpiresAt = useLicenseStore((s) => s.state.trialExpiresAt);
  const trialSkipped = useLicenseStore((s) => s.state.trialSkipped);
  const isInitialized = useLicenseStore((s) => s.isInitialized);

  const subscription = useSubscriptionStore((s) => s.subscription);
  const subscriptionStatus = subscription?.status ?? 'none';
  const subscriptionPeriodEnd = subscription?.currentPeriodEnd ?? null;
  const subscriptionUserId = subscription?.userId ?? null;

  const lastTrialSyncRef = useRef<string | null>(null);
  const lastSubscriptionSyncRef = useRef<string | null>(null);

  // Sync trial data to main process
  useEffect(() => {
    if (!isInitialized) return;

    const api = window.electronAPI?.entitlement;
    if (!api?.syncTrialData) return;

    const trialData = {
      startedAt: toIsoOrNull(trialStartedAt),
      expiresAt: toIsoOrNull(trialExpiresAt),
      skipped: trialSkipped ?? false,
    };

    const trialKey = JSON.stringify(trialData);
    if (lastTrialSyncRef.current === trialKey) return;
    lastTrialSyncRef.current = trialKey;

    api.syncTrialData(trialData).catch((error) => {
      console.error('[EntitlementSync] Failed to sync trial data:', error);
    });

    console.log('[EntitlementSync] Synced trial data:', trialData);
  }, [trialStartedAt, trialExpiresAt, trialSkipped, isInitialized]);

  // Sync subscription data to main process
  useEffect(() => {
    if (!isInitialized) return;

    const api = window.electronAPI?.entitlement;
    if (!api?.syncSubscriptionData) return;

    const subscriptionData = {
      status: subscriptionStatus as
        | 'active'
        | 'trialing'
        | 'past_due'
        | 'canceled'
        | 'unpaid'
        | 'incomplete'
        | 'none',
      currentPeriodEnd: toIsoOrNull(subscriptionPeriodEnd),
      userId: subscriptionUserId,
    };

    const subscriptionKey = JSON.stringify(subscriptionData);
    if (lastSubscriptionSyncRef.current === subscriptionKey) return;
    lastSubscriptionSyncRef.current = subscriptionKey;

    api.syncSubscriptionData(subscriptionData).catch((error) => {
      console.error('[EntitlementSync] Failed to sync subscription data:', error);
    });

    console.log('[EntitlementSync] Synced subscription data:', subscriptionData);
  }, [subscriptionStatus, subscriptionPeriodEnd, subscriptionUserId, isInitialized]);
}

/**
 * Clear entitlement in main process (call on logout/user switch).
 */
export async function clearEntitlement(): Promise<void> {
  const api = window.electronAPI?.entitlement;
  if (!api?.clear) return;

  try {
    await api.clear();
    console.log('[EntitlementSync] Cleared entitlement');
  } catch (error) {
    console.error('[EntitlementSync] Failed to clear entitlement:', error);
  }
}
